import { useMutation, useQueryClient } from 'react-query';

import type { IStandardErrorResponse } from 'src/api/common';
import { useNotifications } from 'src/notifications';
import { queryKeys } from 'src/services/api/constants';
import { apiClient } from 'src/services/api/client';
import { getApiErrorMessage } from 'src/services/api/utils';
import { GET_SNIPPETS_PATH } from 'src/services/api/snippets/constants';
import type {
    ISnippet,
    IUpdateSnippetRequest,
    IUpdateSnippetResponse
} from 'src/services/api/snippets/types';

export const useUpdateSnippet = (id?: number) => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<
        ISnippet,
        IStandardErrorResponse,
        IUpdateSnippetRequest
    >(
        async (payload) => {
            const path = `${GET_SNIPPETS_PATH}/${id}`;
            const res = await apiClient(path, {
                method: 'patch',
                json: payload,
            }).json<IUpdateSnippetResponse>();

            return res.snippet;
        }, {
            onSuccess: (data) => {
                addNotification('Snippet updated', 'success');

                queryClient.setQueryData<ISnippet[]>(queryKeys.snippets, (oldData) => {
                    if (!oldData) {
                        return [];
                    }

                    return oldData.map((snippet) => {
                        return snippet.id === data.id ? data : snippet;
                    });
                });

                queryClient.setQueryData<ISnippet>([queryKeys.snippets, data.id], data);
            }, onError: (error) => {
                const errorMessage = getApiErrorMessage(error) || 'Failed to update snippet';
                addNotification(errorMessage, 'error');
            }
        });
};